import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import QRCode from 'qrcode';
import type { Ticket, TicketCredential, TicketList } from '@gatecheckpro/contracts';
import { Card, EmptyState, LoadingRegion, Notice } from '@gatecheckpro/design-system';
import { Chrome } from './Chrome.tsx';
import { SeatChangeNote, TicketSeatLine } from './MyTicketsPage.tsx';
import { useResource } from '../lib/useResource.ts';
import { isVoid, VoidNotice } from '../lib/ticket-validity.tsx';

/**
 * One ticket's pass, as it is presented at a gate.
 *
 * Rendered as a QR code because that is what a gate reads, and rendered large
 * and high-contrast because it will be read off a cracked phone screen at night
 * by somebody holding a torch. The pass string is also shown as text, so a
 * scanner that will not focus is a delay rather than a refusal.
 *
 * The honesty line is not decoration. A Standard Pass proves the ticket is real;
 * it does not prove the person holding the phone is entitled to it, and a
 * screenshot of it works exactly as well as the original. Saying so here is what
 * stops an attendee — or an organizer reading over their shoulder — believing
 * they bought something they did not.
 */
export function PassPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const tickets = useResource<TicketList>('/api/v1/customer/tickets');
  const credential = useResource<TicketCredential>(
    ticketId === undefined ? null : `/api/v1/customer/tickets/${ticketId}/pass`,
  );

  const ticket: Ticket | undefined =
    tickets.resource.status === 'loaded'
      ? tickets.resource.data.tickets.find((entry) => entry.ticketId === ticketId)
      : undefined;

  return (
    <Chrome>
      <main className="gc-main">
        <div className="gc-container gc-stack gc-stack--loose" style={{ maxWidth: '34rem' }}>
          <div className="gc-page-header">
            <span className="gc-eyebrow">{ticket?.event.title ?? 'Your pass'}</span>
            <h1>{ticket?.ticketName ?? 'Pass'}</h1>
            {/*
              The chair, on the credential itself. Somebody at a door with a
              phone in their hand needs to know where to sit, and the row is not
              printed anywhere else on this screen.
            */}
            <TicketSeatLine seat={ticket?.seat ?? null} />
            {ticket !== undefined && ticket !== null && <SeatChangeNote ticket={ticket} />}
          </div>

          {credential.resource.status === 'loading' && <LoadingRegion label="Loading your pass" />}
          {credential.resource.status === 'failed' && (
            <EmptyState
              title="No pass to show"
              action={
                <Link to="/tickets" className="gc-button gc-button--primary">
                  Your tickets
                </Link>
              }
            >
              This ticket does not exist, or it is not yours.
            </EmptyState>
          )}

          {/*
            A void ticket never renders a scannable code, whatever the pass
            endpoint returned.
            
            Checked here rather than trusted to the server refusing the request:
            a credential that was minted while the ticket was live is still a
            perfectly valid signature, and a page that drew it would put a code
            on screen that a gate is going to reject in front of a queue. The
            honest screen says so before anybody travels.
            
            The ticket's own record stays visible underneath.
          */}
          {ticket !== undefined && isVoid(ticket) && (
            <>
              <VoidNotice ticket={ticket} />
              <Card title="This ticket" eyebrow={ticket.ticketNumber}>
                <div className="gc-stack gc-stack--tight">
                  <span>{ticket.event.title}</span>
                  {ticket.event.venueName !== null && (
                    <span className="gc-muted">{ticket.event.venueName}</span>
                  )}
                  <span className="gc-muted">Order {ticket.orderNumber}</span>
                </div>
              </Card>
            </>
          )}

          {ticket !== undefined && isVoid(ticket)
            ? null
            : credential.resource.status === 'loaded' && (
                <>
                  <PassCode pass={credential.resource.data.pass} />

                  <Card title="This ticket" eyebrow={ticket?.ticketNumber ?? ''}>
                    <div className="gc-stack gc-stack--tight">
                      {ticket !== undefined && (
                        <>
                          <span>{ticket.event.title}</span>
                          {ticket.event.venueName !== null && (
                            <span className="gc-muted">{ticket.event.venueName}</span>
                          )}
                          <span className="gc-muted">Order {ticket.orderNumber}</span>
                        </>
                      )}
                      <span className="gc-muted">
                        Standard pass · expires{' '}
                        {new Date(credential.resource.data.expiresAt).toLocaleString()}
                      </span>
                    </div>
                  </Card>

                  <Notice tone="info" title="What this pass proves">
                    {credential.resource.data.honesty}
                  </Notice>

                  <details>
                    <summary>Show the pass as text</summary>
                    <p className="gc-mono" style={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
                      {credential.resource.data.pass}
                    </p>
                    <p className="gc-muted">
                      If a scanner will not read the code, gate staff can enter this instead.
                    </p>
                  </details>
                </>
              )}

          <p className="gc-muted">
            <Link to="/tickets">All your tickets</Link>
          </p>
        </div>
      </main>
    </Chrome>
  );
}

/**
 * The scannable code.
 *
 * Drawn to a data URL rather than a canvas element so it survives being
 * screenshotted, zoomed and rotated by the browser, and so a failure to encode
 * is a visible message rather than a blank square that looks like a bug in the
 * gate's scanner.
 */
function PassCode({ pass }: { pass: string }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(pass, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 512,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [pass]);

  if (failed) {
    return (
      <Notice tone="critical" title="This pass could not be drawn">
        Show the text version below to gate staff.
      </Notice>
    );
  }

  return (
    <div className="gc-pass">
      <div className="gc-pass__frame">
        {dataUrl === null ? (
          <LoadingRegion label="Drawing your pass" />
        ) : (
          <img
            src={dataUrl}
            alt="Your ticket pass, as a scannable code"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        )}
      </div>
      <p className="gc-muted" style={{ maxWidth: '34ch' }}>
        Turn your screen brightness up before you reach the gate.
      </p>
    </div>
  );
}
